import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import rentsService from "../services/rentsService";
import { useAuth } from "../contexts/AuthContext";
import styles from "./RentsNoticeToQuit.module.css";

const RentsNoticeToQuit = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filteredNotices, setFilteredNotices] = useState([]);
  const [sortColumn, setSortColumn] = useState("date_notice");
  const [sortDirection, setSortDirection] = useState("desc");
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    creator: true,
    guardian: true,
    room: true,
    property: true,
    notice_date: true,
    actions: true,
  });
  const tableRef = useRef(null);
  const columnMenuRef = useRef(null);

  // Load data when page/pageSize changes
  useEffect(() => {
    loadNoticesToQuit();
  }, [currentPage, pageSize]);

  // Force refresh when component mounts (e.g., after navigating from create page)
  useEffect(() => {
    const handleFocus = () => {
      console.log('🔄 [FRONTEND] Window focused, refreshing NTQ list...');
      loadNoticesToQuit();
    };
    
    // Refresh when window regains focus (user returns to tab)
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    filterNotices();
  }, [notices, searchTerm, sortColumn, sortDirection]);

  // Close column visibility menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setShowColumnVisibility(false);
      }
    };

    if (showColumnVisibility) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnVisibility]);

  const loadNoticesToQuit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Call real backend API
      const response = await rentsService.getNoticeToQuitList();
      
      console.log('🔍 [FRONTEND] NTQ List Response:', {
        hasResponse: !!response,
        hasSuccess: response?.success,
        hasData: !!response?.data,
        dataType: Array.isArray(response?.data) ? 'array' : typeof response?.data,
        dataLength: Array.isArray(response?.data) ? response.data.length : 'N/A',
        responseKeys: response ? Object.keys(response) : []
      });

      // Handle different response structures
      let noticesData = null;
      if (response?.success && response?.data) {
        noticesData = response.data;
      } else if (Array.isArray(response)) {
        noticesData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        noticesData = response.data;
      } else {
        console.warn('⚠️ [FRONTEND] Unexpected response structure:', response);
        noticesData = [];
      }

      if (noticesData && Array.isArray(noticesData)) {
        // Transform backend data to match frontend structure
        const transformedNotices = noticesData.map(ntq => ({
          id: ntq.id,
          rentId: ntq.rent_id,
          creator: ntq.created_by_user || ntq.creator || 'N/A',
          guardian: ntq.guardian?.full_name || ntq.guardian || 'N/A',
          guardianId: ntq.guardian?.id || ntq.guardian_id,
          room: ntq.room_name || 'N/A',
          roomName: ntq.room_name || 'N/A',
          property: ntq.property_name || 'N/A',
          propertyId: ntq.property_id,
          propertyName: ntq.property_name || 'N/A',
          notice_date: ntq.date_notice,
          date_notice: ntq.date_notice,
          room_status_id: ntq.room_status_id || ntq.status_id || null, // Room status (2 = NOTICE_GIVEN)
          file: ntq.file_path || ntq.file,
        }));

        console.log(`✅ [FRONTEND] Loaded ${transformedNotices.length} NTQ records`);
        setNotices(transformedNotices);
        setTotalRecords(transformedNotices.length);
      } else {
        console.error('❌ [FRONTEND] No valid data array found in response');
        throw new Error(response?.message || 'Failed to load data');
      }
    } catch (err) {
      console.error("Error loading notices to quit:", err);
      setError(err.message || "Failed to load notices to quit");
      setNotices([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const filterNotices = () => {
    let filtered = notices;

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(notice =>
        (notice.creator || '').toLowerCase().includes(searchLower) ||
        (notice.guardian || '').toLowerCase().includes(searchLower) ||
        (notice.room || '').toLowerCase().includes(searchLower) ||
        (notice.property || '').toLowerCase().includes(searchLower) ||
        (notice.notice_date || '').toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];

      if (sortColumn === 'date_notice') {
        // Handle null dates - null dates go to the end
        if (!aValue && !bValue) return 0;
        if (!aValue) return 1; // null goes to end
        if (!bValue) return -1; // null goes to end
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else {
        // Handle null/undefined values for string sorting
        aValue = aValue != null ? String(aValue).toLowerCase() : '';
        bValue = bValue != null ? String(bValue).toLowerCase() : '';
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredNotices(filtered);
    // Reset to first page when filtering
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not provided";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleExportCSV = () => {
    const csvContent = generateCSV();
    downloadFile(csvContent, 'notice-to-quit.csv', 'text/csv');
  };

  const handleExportPDF = async () => {
    try {
      console.log("📄 [FRONTEND] Starting PDF export for Notice To Quit...");
      console.log("📊 [DEBUG] Filtered notices count:", filteredNotices.length);
      console.log("📊 [DEBUG] Visible columns:", visibleColumns);
      
      // Show loading state
      const pdfButton = document.querySelector('button[title="Export as PDF"]');
      if (pdfButton) {
        pdfButton.disabled = true;
        pdfButton.innerHTML = '<i className="fa fa-spinner fa-spin"></i> Generating...';
      }
      
      // Check if we have data to export
      if (!filteredNotices || filteredNotices.length === 0) {
        alert("No data available to export. Please check your filters or try refreshing the page.");
        return;
      }
      
      // Import jsPDF and autoTable with proper error handling
      console.log("📦 [DEBUG] Importing jsPDF...");
      const { jsPDF } = await import('jspdf');
      console.log("📦 [DEBUG] jsPDF imported successfully");
      
      console.log("📦 [DEBUG] Importing jspdf-autotable...");
      await import('jspdf-autotable');
      console.log("📦 [DEBUG] jspdf-autotable imported successfully");
      
      const doc = new jsPDF('landscape');
      console.log("📄 [DEBUG] jsPDF document created");
      
      // Add title and metadata
      doc.setFontSize(16);
      doc.text('Guardian Notice To Quit Report', 20, 20);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString('en-GB')}`, 20, 30);
      doc.text(`Total Records: ${filteredNotices.length}`, 20, 40);
      
      // Add search filter info if applicable
      if (searchTerm) {
        doc.text(`Filtered by: "${searchTerm}"`, 20, 50);
      }
      
      // Prepare table data based on visible columns
      const headers = [];
      const data = [];
      
      // Add headers based on visible columns
      if (visibleColumns.creator) headers.push('Creator');
      if (visibleColumns.guardian) headers.push('Guardian');
      if (visibleColumns.room) headers.push('Room');
      if (visibleColumns.property) headers.push('Property');
      if (visibleColumns.notice_date) headers.push('Notice Date');
      
      console.log("📊 [DEBUG] Headers prepared:", headers);
      
      // Add data rows
      filteredNotices.forEach((notice, index) => {
        const row = [];
        if (visibleColumns.creator) row.push(String(notice.creator || ''));
        if (visibleColumns.guardian) row.push(String(notice.guardian || ''));
        if (visibleColumns.room) row.push(String(notice.room || ''));
        if (visibleColumns.property) row.push(String(notice.property || ''));
        if (visibleColumns.notice_date) row.push(String(formatDate(notice.notice_date) || ''));
        data.push(row);
        
        // Log first few rows for debugging
        if (index < 3) {
          console.log(`📊 [DEBUG] Row ${index}:`, row);
        }
      });
      
      console.log("📊 [DEBUG] Data prepared, rows count:", data.length);
      
      // Check if we have headers and data
      if (headers.length === 0) {
        alert("No columns are visible. Please show at least one column to export.");
        return;
      }
      
      if (data.length === 0) {
        alert("No data rows to export.");
        return;
      }
      
      // Add table using autoTable
      console.log("📊 [DEBUG] Creating autoTable...");
      
      try {
        doc.autoTable({
          head: [headers],
          body: data,
          startY: searchTerm ? 60 : 50,
          styles: {
            fontSize: 8,
            cellPadding: 3,
            overflow: 'linebreak',
            halign: 'left',
          },
          headStyles: {
            fillColor: [66, 139, 202],
            textColor: 255,
            fontStyle: 'bold',
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245],
          },
          margin: { top: searchTerm ? 60 : 50 },
          didDrawPage: (data) => {
            // Add page numbers
            const pageCount = doc.internal.getNumberOfPages();
            const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
            doc.setFontSize(8);
            doc.text(`Page ${currentPage} of ${pageCount}`, 
              doc.internal.pageSize.width - 30, 
              doc.internal.pageSize.height - 10);
          }
        });
      } catch (autoTableError) {
        console.warn("⚠️ [DEBUG] autoTable failed, using fallback method:", autoTableError);
        
        // Fallback: Create table manually
        let yPosition = searchTerm ? 60 : 50;
        const colWidth = (doc.internal.pageSize.width - 40) / headers.length;
        
        // Draw headers
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        headers.forEach((header, index) => {
          doc.text(header, 20 + (index * colWidth), yPosition);
        });
        
        yPosition += 10;
        
        // Draw data rows
        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        data.forEach((row, rowIndex) => {
          if (yPosition > doc.internal.pageSize.height - 20) {
            doc.addPage();
            yPosition = 20;
          }
          
          row.forEach((cell, colIndex) => {
            const cellText = String(cell || '').substring(0, 20); // Limit text length
            doc.text(cellText, 20 + (colIndex * colWidth), yPosition);
          });
          
          yPosition += 6;
        });
      }
      
      console.log("📊 [DEBUG] autoTable created successfully");
      
      // Save PDF
      const fileName = `notice_to_quit_${new Date().toISOString().split('T')[0]}.pdf`;
      console.log("💾 [DEBUG] Saving PDF as:", fileName);
      doc.save(fileName);
      
      console.log("✅ [FRONTEND] PDF export completed successfully");
      
      // Show success message
      alert("PDF exported successfully!");
      
    } catch (error) {
      console.error("❌ [FRONTEND] Error generating PDF:", error);
      console.error("❌ [DEBUG] Error details:", {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      alert(`Error generating PDF: ${error.message}. Please try again.`);
    } finally {
      // Restore button state
      const pdfButton = document.querySelector('button[title="Export as PDF"]');
      if (pdfButton) {
        pdfButton.disabled = false;
        pdfButton.innerHTML = '<i className="fa fa-file-pdf"></i> PDF';
      }
    }
  };

  const handlePrintTable = () => {
    window.print();
  };

  const generateCSV = () => {
    const headers = ['Creator', 'Guardian', 'Room', 'Property', 'Notice Date'];
    const csvData = filteredNotices.map(notice => [
      notice.creator,
      notice.guardian,
      notice.room,
      notice.property,
      formatDate(notice.notice_date)
    ]);

    return [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
  };

  const downloadFile = (content, filename, contentType) => {
    const blob = new Blob([content], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Action button handlers - matching Laravel functionality exactly
  const handleManageNTQ = (rentId, noticeId) => {
    // Navigate to edit page - matches Laravel route: rents.notice_to_quit.edit
    // Laravel route: {rent}/notice-to-quit/edit (no noticeId in URL, just rentId)
    if (rentId) {
      navigate(`/rents/${rentId}/notice-to-quit/edit`);
    } else {
      console.error('Cannot navigate: rentId is missing');
    }
  };

  const handleUploadDocument = (rentId) => {
    // Navigate to upload page - matches Laravel route: rents.notice_to_quit.uploadFile
    navigate(`/rents/${rentId}/notice-to-quit/uploadFile`);
  };

  const handleDownloadDocument = async (rentId) => {
    try {
      // Call real API with presigned URL
      await rentsService.downloadNoticeToQuit(rentId);
      toast.success("Document downloaded successfully");
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error(error.message || "Failed to download document");
    }
  };

  const handleDeleteNTQ = async (rentId, ntqId) => {
    if (window.confirm("Are you sure you want to delete this notice to quit?")) {
      try {
        const response = await rentsService.deleteNoticeToQuit(rentId, ntqId);

        if (response.success) {
          toast.success("Notice to quit deleted successfully");
          // Refresh the list
          loadNoticesToQuit();
        } else {
          throw new Error(response.message || 'Delete failed');
        }
      } catch (error) {
        console.error("Error deleting NTQ:", error);
        toast.error(error.message || "Failed to delete notice to quit");
      }
    }
  };

  const toggleColumnVisibility = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const toggleColumnMenu = () => {
    setShowColumnVisibility(!showColumnVisibility);
  };

  // Check if room has NOTICE_GIVEN status (room status_id = 2)
  // Laravel condition: $model->status_id == config('property.room.status_reverse.NOTICE_GIVEN')
  // NOTICE_GIVEN = 2 (from room config)
  const isNoticeGiven = (notice) => {
    return notice.room_status_id === 2; // Room status NOTICE_GIVEN
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.alertDanger}>{error}</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        {/* Breadcrumb */}
        <div className="breadcrumb-row">
          <span
            className="breadcrumb-main"
            style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
            onClick={() => navigate('/dashboard')}
          >
            Dashboard
          </span>
          <span
            className="breadcrumb-main"
            style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
            onClick={() => navigate('/rents')}
          >
            <span className="breadcrumb-sep">/</span>
            Rents
          </span>
          <div
            style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
          >
            <span className="breadcrumb-sep">/</span>
            <span className="breadcrumb-properties">Notice To Quit</span>
          </div>
        </div>

        <div className={styles.panelStandard}>
          <div className={styles.panelHeading}>
            <h3 className={styles.panelTitle}>Guardian Notice To Quit</h3>
          </div>
          <div className={styles.panelBody}>
            {notices.length > 0 ? (
              <div className={styles.dataTableWrapper}>
                {/* DataTable Controls */}
                <div className={styles.dataTableControls}>
                  <div className={styles.entriesControl}>
                    <label>Show</label>
                    <select
                      value={pageSize}
                      onChange={handlePageSizeChange}
                      className={styles.entriesSelect}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span>entries</span>
                  </div>

                  <div className={styles.searchControl}>
                    <label htmlFor="search">Search:</label>
                    <input
                      type="text"
                      id="search"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      placeholder="Search..."
                      className={styles.searchInput}
                    />
                  </div>

                  <div className={styles.exportButtons}>
                    <button
                      className={`${styles.btn} ${styles.btnExport}`}
                      onClick={handleExportCSV}
                      title="Export as CSV"
                    >
                      <i className="fa fa-file-csv"></i>
                      CSV
                    </button>
                    <button
                      className={`${styles.btn} ${styles.btnExport}`}
                      onClick={handleExportPDF}
                      title="Export as PDF"
                    >
                      <i className="fa fa-file-pdf"></i>
                      PDF
                    </button>
                    <button
                      className={`${styles.btn} ${styles.btnExport}`}
                      onClick={handlePrintTable}
                      title="Print Table"
                    >
                      <i className="fa fa-print"></i>
                      Print
                    </button>
                    <div className={styles.columnVisibilityContainer} ref={columnMenuRef}>
                      <button
                        className={`${styles.btn} ${styles.btnExport}`}
                        onClick={toggleColumnMenu}
                        title="Column Visibility"
                      >
                        <i className="fa fa-columns"></i>
                        Columns
                      </button>
                      {showColumnVisibility && (
                        <div className={styles.columnVisibilityMenu}>
                          <h4>Toggle Columns:</h4>
                          {Object.entries(visibleColumns).map(([column, visible]) => (
                            <label key={column} className={styles.columnToggle}>
                              <input
                                type="checkbox"
                                checked={visible}
                                onChange={() => toggleColumnVisibility(column)}
                              />
                              {column.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* DataTable */}
                <div className={styles.tableContainer}>
                  <table ref={tableRef} className={styles.table}>
                    <thead>
                      <tr>
                        {visibleColumns.creator && (
                          <th
                            className={styles.sortable}
                            onClick={() => handleSort('creator')}
                          >
                            Creator
                            {sortColumn === 'creator' && (
                              <span className={styles.sortIcon}>
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                        )}
                        {visibleColumns.guardian && (
                          <th
                            className={styles.sortable}
                            onClick={() => handleSort('guardian')}
                          >
                            Guardian
                            {sortColumn === 'guardian' && (
                              <span className={styles.sortIcon}>
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                        )}
                        {visibleColumns.room && (
                          <th
                            className={styles.sortable}
                            onClick={() => handleSort('room')}
                          >
                            Room
                            {sortColumn === 'room' && (
                              <span className={styles.sortIcon}>
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                        )}
                        {visibleColumns.property && (
                          <th
                            className={styles.sortable}
                            onClick={() => handleSort('property')}
                          >
                            Property
                            {sortColumn === 'property' && (
                              <span className={styles.sortIcon}>
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                        )}
                        {visibleColumns.notice_date && (
                          <th
                            className={styles.sortable}
                            onClick={() => handleSort('date_notice')}
                          >
                            Notice date
                            {sortColumn === 'date_notice' && (
                              <span className={styles.sortIcon}>
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                        )}
                        {visibleColumns.actions && (
                          <th className={styles.actionHead}>Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredNotices
                        .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                        .map((notice) => (
                        <tr key={notice.id} className={styles.tableRow}>
                          {visibleColumns.creator && (
                            <td>{notice.creator || '-'}</td>
                          )}
                          {visibleColumns.guardian && (
                            <td>
                              {notice.guardianId ? (
                                <Link
                                  to={`/guardians/${notice.guardianId}`}
                                  className={styles.guardianLink}
                                >
                                  <strong>{notice.guardian || '-'}</strong>
                                </Link>
                              ) : (
                                <strong>{notice.guardian || '-'}</strong>
                              )}
                            </td>
                          )}
                          {visibleColumns.room && (
                            <td>{notice.room || '-'}</td>
                          )}
                          {visibleColumns.property && (
                            <td>
                              {notice.propertyId ? (
                                <Link
                                  to={`/properties/${notice.propertyId}`}
                                  className={styles.propertyLink}
                                >
                                  <strong>{notice.property || '-'}</strong>
                                </Link>
                              ) : (
                                <strong>{notice.property || '-'}</strong>
                              )}
                            </td>
                          )}
                          {visibleColumns.notice_date && (
                            <td>{formatDate(notice.notice_date)}</td>
                          )}
                          {visibleColumns.actions && (
                            <td className={styles.actionCell}>
                              {/* Only show buttons if user has permission and notice is given */}
                              {hasPermission('rents.notice_to_quit.manage') && isNoticeGiven(notice) && (
                                <div className={styles.actionButtons}>
                                  <button
                                    className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary} ${styles.btnAction}`}
                                    onClick={() => handleManageNTQ(notice.rentId, notice.id)}
                                    title="Manage NTQ"
                                  >
                                    Manage NTQ
                                  </button>
                                  <button
                                    className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary} ${styles.btnWarning}`}
                                    onClick={() => handleUploadDocument(notice.rentId)}
                                    title="Upload document"
                                  >
                                    Upload document
                                  </button>
                                  {notice.file && (
                                    <button
                                      className={`${styles.btn} ${styles.btnSm} ${styles.btnSuccess} ${styles.hiddenPrintLink}`}
                                      onClick={() => handleDownloadDocument(notice.rentId)}
                                      title="Download document"
                                    >
                                      Download
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Info */}
                <div className={styles.paginationInfo}>
                  <div className={styles.paginationText}>
                    Showing {filteredNotices.length > 0 ? ((currentPage - 1) * pageSize) + 1 : 0} to {Math.min(currentPage * pageSize, filteredNotices.length)} of {filteredNotices.length} entries
                  </div>
                  <div className={styles.paginationControls}>
                    <button
                      className={`${styles.btn} ${styles.btnPagination}`}
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      Previous
                    </button>
                    <span className={styles.pageInfo}>
                      Page {currentPage} of {Math.ceil(filteredNotices.length / pageSize) || 1}
                    </span>
                    <button
                      className={`${styles.btn} ${styles.btnPagination}`}
                      disabled={currentPage >= Math.ceil(filteredNotices.length / pageSize)}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className={styles.noData}>
                No guardian notice to quit!
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentsNoticeToQuit;
